/**
 * Flat project list for Meridian Associates.
 *
 * Each project is owned by one client (`clientId`) unless it is the org-level
 * "General" bucket (`clientId: null`), which holds non-billable firm-wide time.
 *
 * Mix of `billable` and `general` types. Cross-referenced by:
 *   - CLIENTS (clients.ts) via clientId
 *   - RULES (rules.ts) via projectId
 *   - TIMESHEETS (timesheets.ts) via projectId
 *
 * Dates derive from FROZEN_NOW.
 */

import type { Project } from '@/types/entities';
import { FROZEN_NOW_MS } from '@/lib/frozenTime';

const DAY_MS = 24 * 60 * 60 * 1000;
const iso = (daysAgo: number): string => new Date(FROZEN_NOW_MS - daysAgo * DAY_MS).toISOString();

export const PROJECTS = [
  // ── Org-level (clientId: null) ────────────────────────────────────────────
  {
    id: 'prj-general',
    clientId: null,
    name: 'General',
    type: 'general',
    createdAt: iso(400),
    systemOwned: false,
  },
  {
    id: 'prj-firm-admin',
    clientId: null,
    name: 'Firm Administration',
    type: 'general',
    createdAt: iso(380),
    systemOwned: false,
  },
  {
    id: 'prj-internal-training',
    clientId: null,
    name: 'Internal Training',
    type: 'general',
    createdAt: iso(300),
    systemOwned: false,
  },
  {
    id: 'prj-knowledge-mgmt',
    clientId: null,
    name: 'Knowledge Management',
    type: 'general',
    createdAt: iso(280),
    systemOwned: false,
  },

  // ── Meridian Capital ──────────────────────────────────────────────────────
  {
    id: 'prj-meridian-restructuring',
    clientId: 'cli-meridian-capital',
    name: 'Corporate Restructuring',
    type: 'billable',
    createdAt: iso(200),
    systemOwned: false,
  },
  {
    id: 'prj-meridian-mna',
    clientId: 'cli-meridian-capital',
    name: 'M&A Due Diligence',
    type: 'billable',
    createdAt: iso(180),
    // Task 201 D5 — full taxonomy + external refs + domain bindings so the
    // manage UIs render every new field with sample data in mockup mode.
    description:
      'Due-diligence workstream for Meridian Capital acquisitions. Covers buy-side analyst review of public + non-public targets.',
    aliases: ['MNA-MERIDIAN', 'mna-meridian', 'Project Phoenix'],
    keywords: ['due-diligence', 'acquisition', 'buyside', 'merger', 'meridian'],
    externalRefs: {
      jiraProjectKey: 'MNAMERIDIAN',
      githubRepos: ['meridian/mna-models', 'meridian/dd-workpapers'],
      slackChannels: ['#mna-meridian', '#mna-meridian-alerts'],
    },
    domainBindings: [
      {
        id: 'pdb-meridian-mna-1',
        projectId: 'prj-meridian-mna',
        orgId: 'org-meridian',
        host: 'dataroom.meridiancap.com',
        kind: 'exact',
        confidence: 1.0,
        createdAt: iso(170),
      },
      {
        id: 'pdb-meridian-mna-2',
        projectId: 'prj-meridian-mna',
        orgId: 'org-meridian',
        host: '*.intralinks.com',
        kind: 'suffix',
        confidence: 1.0,
        createdAt: iso(165),
      },
      {
        id: 'pdb-meridian-mna-3',
        projectId: 'prj-meridian-mna',
        orgId: 'org-meridian',
        host: 'jira.meridiancap.com',
        kind: 'exact',
        confidence: 1.0,
        createdAt: iso(160),
      },
    ],
    systemOwned: false,
  },
  {
    id: 'prj-meridian-regulatory',
    clientId: 'cli-meridian-capital',
    name: 'Regulatory Advisory',
    type: 'billable',
    createdAt: iso(160),
    systemOwned: false,
  },

  // ── TechVenture Labs ──────────────────────────────────────────────────────
  {
    id: 'prj-techventure-patents',
    clientId: 'cli-techventure-labs',
    name: 'Patent Portfolio Review',
    type: 'billable',
    createdAt: iso(220),
    systemOwned: false,
  },
  {
    id: 'prj-techventure-ip-strategy',
    clientId: 'cli-techventure-labs',
    name: 'IP Strategy',
    type: 'billable',
    createdAt: iso(150),
    systemOwned: false,
  },
  {
    id: 'prj-techventure-trade-secrets',
    clientId: 'cli-techventure-labs',
    name: 'Trade Secret Audit',
    type: 'billable',
    createdAt: iso(90),
    systemOwned: false,
  },

  // ── Apex Holdings ─────────────────────────────────────────────────────────
  {
    id: 'prj-apex-acquisition',
    clientId: 'cli-apex-holdings',
    name: 'Acquisition Review',
    type: 'billable',
    createdAt: iso(170),
    systemOwned: false,
  },
  {
    id: 'prj-apex-governance',
    clientId: 'cli-apex-holdings',
    name: 'Board Governance',
    type: 'billable',
    createdAt: iso(130),
    systemOwned: false,
  },
  {
    id: 'prj-apex-compliance',
    clientId: 'cli-apex-holdings',
    name: 'Compliance Review',
    type: 'billable',
    createdAt: iso(100),
    systemOwned: false,
  },

  // ── Pinnacle Financial ────────────────────────────────────────────────────
  {
    id: 'prj-pinnacle-regulatory',
    clientId: 'cli-pinnacle-financial',
    name: 'Regulatory Compliance',
    type: 'billable',
    createdAt: iso(140),
    systemOwned: false,
  },
  {
    id: 'prj-pinnacle-tax',
    clientId: 'cli-pinnacle-financial',
    name: 'Tax Advisory',
    type: 'billable',
    createdAt: iso(110),
    systemOwned: false,
  },

  // ── Anderson Family Trust ─────────────────────────────────────────────────
  {
    id: 'prj-anderson-estate',
    clientId: 'cli-anderson-trust',
    name: 'Estate Planning',
    type: 'billable',
    createdAt: iso(240),
    systemOwned: false,
  },
  {
    id: 'prj-anderson-wealth',
    clientId: 'cli-anderson-trust',
    name: 'Wealth Transfer Strategy',
    type: 'billable',
    createdAt: iso(210),
    systemOwned: false,
  },

  // ── Stellar Dynamics ──────────────────────────────────────────────────────
  {
    id: 'prj-stellar-contract',
    clientId: 'cli-stellar-dynamics',
    name: 'Contract Negotiation',
    type: 'billable',
    createdAt: iso(120),
    systemOwned: false,
  },
  {
    id: 'prj-stellar-litigation',
    clientId: 'cli-stellar-dynamics',
    name: 'Commercial Litigation',
    type: 'billable',
    createdAt: iso(85),
    systemOwned: false,
  },

  // ── Quantum Biotech ───────────────────────────────────────────────────────
  {
    id: 'prj-quantum-ip-licensing',
    clientId: 'cli-quantum-biotech',
    name: 'IP Licensing',
    type: 'billable',
    createdAt: iso(95),
    systemOwned: false,
  },
  {
    id: 'prj-quantum-fda',
    clientId: 'cli-quantum-biotech',
    name: 'FDA Regulatory',
    type: 'billable',
    createdAt: iso(70),
    systemOwned: false,
  },

  // ── Community Legal (pro bono) ────────────────────────────────────────────
  {
    id: 'prj-community-probono',
    clientId: 'cli-community-legal',
    name: 'Pro Bono — Legal Aid',
    type: 'billable',
    createdAt: iso(260),
    systemOwned: false,
  },

  // ── Harmon Industries ─────────────────────────────────────────────────────
  {
    id: 'prj-harmon-litigation',
    clientId: 'cli-harmon-industries',
    name: 'Product Liability Litigation',
    type: 'billable',
    createdAt: iso(75),
    systemOwned: false,
  },
  {
    id: 'prj-harmon-employment',
    clientId: 'cli-harmon-industries',
    name: 'Employment Advisory',
    type: 'billable',
    createdAt: iso(60),
    systemOwned: false,
  },

  // ── Pacific Ventures ──────────────────────────────────────────────────────
  {
    id: 'prj-pacific-vc',
    clientId: 'cli-pacific-ventures',
    name: 'Venture Capital Formation',
    type: 'billable',
    createdAt: iso(50),
    systemOwned: false,
  },
  {
    id: 'prj-pacific-portfolio',
    clientId: 'cli-pacific-ventures',
    name: 'Portfolio Company Counsel',
    type: 'billable',
    createdAt: iso(40),
    systemOwned: false,
  },

  // ── Northwood Partners ────────────────────────────────────────────────────
  {
    id: 'prj-northwood-real-estate',
    clientId: 'cli-northwood-partners',
    name: 'Real Estate Transactions',
    type: 'billable',
    createdAt: iso(55),
    systemOwned: false,
  },
  {
    id: 'prj-northwood-financing',
    clientId: 'cli-northwood-partners',
    name: 'Commercial Financing',
    type: 'billable',
    createdAt: iso(30),
    systemOwned: false,
  },

  // ── Crestview Holdings ────────────────────────────────────────────────────
  {
    id: 'prj-crestview-structuring',
    clientId: 'cli-crestview-holdings',
    name: 'Entity Structuring',
    type: 'billable',
    createdAt: iso(35),
    systemOwned: false,
  },
  {
    id: 'prj-crestview-tax-planning',
    clientId: 'cli-crestview-holdings',
    name: 'Tax Planning',
    type: 'billable',
    createdAt: iso(20),
    systemOwned: false,
  },
  {
    id: 'prj-crestview-succession',
    clientId: 'cli-crestview-holdings',
    name: 'Succession Planning',
    type: 'billable',
    createdAt: iso(12),
    systemOwned: false,
  },
] as const satisfies readonly Project[];
